<script setup>
import { defineProps, defineEmits, computed, ref, provide, toRef } from 'vue';
import { debounce } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';
import Message from './Message.vue';
import { MESSAGE_TYPES } from './constants.js';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import { useStore } from 'vuex';

import {
  searchContacts,
  fetchContactsForward,
  fetchContactableInboxes,
  prepareNewMessagePayload,
  getTopLastActivity,
} from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';



/**
 * Props definition for the component
 */
const props = defineProps({
  currentUserId: {
    type: Number,
    required: true,
  },
  firstUnreadId: {
    type: Number,
    default: null,
  },
  isAnEmailChannel: {
    type: Boolean,
    default: false,
  },
  inboxSupportsReplyTo: {
    type: Object,
    default: () => ({ incoming: false, outgoing: false }),
  },
  messages: {
    type: Array,
    default: () => [],
  },
  currentUser: {
    type: Object,
    default: () => ({}),
  },
  isAWhatsAppChannel: {
    type: Boolean,
    default: false,
  },
  isGroupConversation: {
    type: Boolean,
    default: false,
  },
});

provide('allMessages', toRef(props, 'messages'));

const emit = defineEmits([
  'toggle-selection-mode', 
  'select-message', 
  'mounted'
]);

const store = useStore();

// Estado LOCAL igual ao ConversationBox.vue
const isSelectionMode = ref(false);
const selectedMessages = ref([]);
const selectedSenderIdentifier = ref(null);

// Estado do modal de forward
const showForwardModal = ref(false);
const contacts = ref([]);
const isFetchingContacts = ref(false);
const searchQuery = ref('');
const selectedContacts = ref([]);
const showContactsDropdown = ref(false);
const currentPage = ref(1);
const hasMoreContacts = ref(true);

const allMessages = computed(() => {
  return useCamelCase(props.messages, { deep: true });
});

const contactsList = computed(() => {
  const checkedContacts = selectedContacts.value.map(contact => {
    return {
      id: contact.id,
      label: contact.email ? `${contact.name} (${contact.email})` : contact.name || contact.phone_number || 'Sem nome',
      value: contact.id,
      thumbnail: {
        name: contact.name || '',
        src: typeof contact.thumbnail === 'string' ? contact.thumbnail : contact.thumbnail?.src || '',
      },
      isChecked: true,
    };
  });

  const uncheckedContacts = contacts.value
    .filter(contact => !selectedContacts.value.some(selected => selected.id === contact.id))
    .map(({ name, id, thumbnail, email, phone_number }) => ({
      id,
      label: email ? `${name} (${email})` : name || phone_number || 'Sem nome',
      value: id,
      thumbnail: {
        name: name || '',
        src: typeof thumbnail === 'string' ? thumbnail : thumbnail?.src || '',
      },
      isChecked: false,
    }));

  return [
    ...checkedContacts,
    ...(checkedContacts.length ? [{ isDivider: true }] : []),
    ...uncheckedContacts,
  ];
});

const shouldGroupWithNext = (index, searchList) => {
  if (index === searchList.length - 1) return false;

  const current = searchList[index];
  const next = searchList[index + 1];

  if (next.status === 'failed') return false;

  const nextSenderId = next.senderId ?? next.sender?.id;
  const currentSenderId = current.senderId ?? current.sender?.id;
  const hasSameSender = nextSenderId === currentSenderId;

  const nextMessageType = next.messageType;
  const currentMessageType = current.messageType;

  const areBothTemplates =
    nextMessageType === MESSAGE_TYPES.TEMPLATE &&
    currentMessageType === MESSAGE_TYPES.TEMPLATE;

  if (!hasSameSender || areBothTemplates) return false;

  if (currentMessageType !== nextMessageType) return false;

  return Math.floor(next.createdAt / 60) === Math.floor(current.createdAt / 60);
};

const getInReplyToMessage = parentMessage => {
  if (!parentMessage) return null;

  const inReplyToMessageId =
    parentMessage.contentAttributes?.inReplyTo ??
    parentMessage.content_attributes?.in_reply_to;

  if (!inReplyToMessageId) return null;

  const replyMessage = props.messages?.find(
    message => message.id === inReplyToMessageId
  );

  return replyMessage ? useCamelCase(replyMessage) : null;
};

const getForwardedMessage = parentMessage => {
  if (!parentMessage) return {};
  const isForwarded = parentMessage.content_attributes?.to_forward;
  if (!isForwarded) return {};
  return parentMessage;
};

// Métodos do modal de forward
const performContactSearch = async (query) => {
  isFetchingContacts.value = true;
  try {
    const contactsResponse = await searchContacts({
      keys: ['email', 'phone_number', 'name'],
      query,
    });
    const newContacts = contactsResponse || [];
    contacts.value = newContacts.filter(contact => contact.identifier !== selectedSenderIdentifier.value);
  } catch (error) {
    console.error('Erro ao buscar contatos:', error);
    contacts.value = [];
  } finally {
    isFetchingContacts.value = false;
  }
};

const onContactSearch = (query) => {
  const debouncedSearch = debounce((q) => {
    performContactSearch(q);
  }, 500, false);
  debouncedSearch(query);
};

const fetchRecentContactsForSearch = async () => {
  isFetchingContacts.value = true;
  try {
    currentPage.value = 1;
    hasMoreContacts.value = true;
    const contactsResponse = await fetchContactsForward(currentPage.value);
    contacts.value = contactsResponse.filter(contact => contact.identifier !== selectedSenderIdentifier.value);
    if (contactsResponse.length < 15) {
      hasMoreContacts.value = false;
    }
  } catch (error) {
    console.error('Erro ao buscar contatos recentes:', error);
    contacts.value = [];
    hasMoreContacts.value = false;
  } finally {
    isFetchingContacts.value = false;
  }
};

const handleContactListScroll = async (e) => {
  const contactList = e.target;
  const scrollTop = contactList.scrollTop;
  const scrollHeight = contactList.scrollHeight;
  const clientHeight = contactList.clientHeight;

  if (
    scrollHeight - scrollTop - clientHeight < 100 &&
    !isFetchingContacts.value &&
    hasMoreContacts.value
  ) {
    isFetchingContacts.value = true;
    try {
      currentPage.value += 1;
      const newContacts = await fetchContactsForward(currentPage.value);
      const filteredContacts = newContacts.filter(
        contact => contact.identifier !== selectedSenderIdentifier.value
      );
      contacts.value = [...contacts.value, ...filteredContacts];
      if (newContacts.length < 15) {
        hasMoreContacts.value = false;
      }
    } catch (error) {
      console.error('Erro ao carregar mais contatos:', error);
      hasMoreContacts.value = false;
    } finally {
      isFetchingContacts.value = false;
    }
  }
};

const handleForward = async () => {
  
  showForwardModal.value = true;
  showContactsDropdown.value = true;
  isFetchingContacts.value = true;
  currentPage.value = 1;
  hasMoreContacts.value = true;
  contacts.value = [];
  
  try {
    const contactsResponse = await getTopLastActivity(currentPage.value);
    contacts.value = contactsResponse.filter(contact => contact.identifier !== selectedSenderIdentifier.value);
    if (contactsResponse.length < 15) {
      hasMoreContacts.value = false;
    }
  } catch (error) {
    console.error('Erro ao buscar contatos recentes:', error);
    hasMoreContacts.value = false;
  } finally {
    isFetchingContacts.value = false;
  }
};

const handleContactSearch = (value) => {
  searchQuery.value = value;
  showContactsDropdown.value = true;
  currentPage.value = 1;
  hasMoreContacts.value = true;
  if (value.trim() === '') {
    fetchRecentContactsForSearch();
  } else {
    onContactSearch(value);
  }
};

const toggleContactSelection = (contact) => {
  const index = selectedContacts.value.findIndex(c => c.id === contact.id);
  if (index === -1) {
    const fullContact = contacts.value.find(c => c.id === contact.id) || contact;
    selectedContacts.value = [...selectedContacts.value, fullContact];
  } else {
    selectedContacts.value = selectedContacts.value.filter(c => c.id !== contact.id);
  }
};

const closeForwardModal = () => {
  showForwardModal.value = false;
  contacts.value = [];
  searchQuery.value = '';
  selectedContacts.value = [];
  showContactsDropdown.value = false;
  selectedSenderIdentifier.value = null;
};

const confirmForward = async () => {
  if (selectedContacts.value.length) {
    try {
      for (const contact of selectedContacts.value) {
        const contactInboxes = await fetchContactableInboxes(contact.id);
        for (const message of selectedMessages.value) {

          const targetInbox = contactInboxes.find(inbox => inbox.id === message.inboxId);
          if (!targetInbox) {
            console.error('Inbox não encontrada para inboxId:', message.inboxId);
            continue;
          }

          const payload = prepareNewMessagePayload({
            targetInbox: targetInbox,
            selectedContact: contact,
            message: message.content || '',
            currentUser: props.currentUser,
            attachedFiles: message.attachments || [],
            directUploadsEnabled: false,
            forward: true
          });
          
          const isFromWhatsApp = props.isAWhatsAppChannel;
          
          await store.dispatch('contactConversations/create', {
              params: payload,
              isFromWhatsApp,
          });

          
        }
      }
      closeForwardModal();
      clearSelectedMessages();
      useAlert('Mensagem encaminhada com sucesso!');
    } catch (error) {
      console.error('handleForward: Erro ao encaminhar mensagens:', error);
      const errorMessage = error?.response?.data?.error || 'Erro ao encaminhar a mensagem';
      useAlert(errorMessage);
    }
  }
};

// Funções principais - IGUAL ao ConversationBox.vue
const clearSelectedMessages = () => {
  selectedMessages.value = [];
  isSelectionMode.value = false;
  selectedSenderIdentifier.value = null;
};



const handleToggleSelectionMode = (data) => {
  
  isSelectionMode.value = !isSelectionMode.value;
  
  if (!isSelectionMode.value) {
    selectedMessages.value = [];
    selectedSenderIdentifier.value = null;
  } else {
    selectedMessages.value.push(data);
    selectedSenderIdentifier.value = data.sender?.identifier;
  }
  

};

const toggleMessageSelection = (data) => {
  if (selectedMessages.value.some(msg => msg.id === data.id)) {
    selectedMessages.value = selectedMessages.value.filter(msg => msg.id !== data.id);
    if (selectedMessages.value.length === 0) {
      selectedSenderIdentifier.value = null;
    }
  } else {
    selectedMessages.value.push(data);
    selectedSenderIdentifier.value = data.sender?.identifier;
  }
};

const handleContextMenuMounted = () => {
  closeForwardModal();
  clearSelectedMessages();
  emit('mounted');
}
</script>

<template>
  <div>


    <ul class="px-4 bg-n-background">
      <slot name="beforeAll" />
      <template v-for="(message, index) in allMessages" :key="message.id">
        <slot
          v-if="firstUnreadId && message.id === firstUnreadId"
          name="unreadBadge"
        />
        <Message
          v-bind="message"
          :is-email-inbox="isAnEmailChannel"
          :in-reply-to="getInReplyToMessage(message)"
          :group-with-next="shouldGroupWithNext(index, allMessages)"
          :group-with-previous="
            index > 0 && shouldGroupWithNext(index - 1, allMessages)
          "
          :inbox-supports-reply-to="inboxSupportsReplyTo"
          :current-user-id="currentUserId"
          :is-group-conversation="isGroupConversation"
          data-clarity-mask="True"
          :forwarded-message="getForwardedMessage(message)"
          :is-selection-mode="isSelectionMode"
          :is-selected="selectedMessages.some(msg => msg.id === message.id)"
          @toggle-selection-mode="handleToggleSelectionMode"
          @select-message="toggleMessageSelection"
          @mounted="handleContextMenuMounted"
        />
      </template>
      <slot name="after" />
    </ul>

    <!-- Painel de Seleção -->
    <!-- Painel de Seleção -->
    <div
      v-if="isSelectionMode && selectedMessages.length > 0"
      class="message-selection-panel"
    >
      <div class="flex items-center gap-2">
        <button
          class="clear-selection-button"
          :title="$t('CONVERSATION.CONTEXT_MENU.FORWARD.SELECTION_CANCEL')"
          @click="clearSelectedMessages"
        >
          <svg class="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19.6 17.2L14.3 11.9L19.6 6.60005L17.8 4.80005L12.5 10.2L7.2 4.90005L5.4 6.60005L10.7 11.9L5.4 17.2L7.2 19L12.5 13.7L17.8 19L19.6 17.2Z"/>
          </svg>
        </button>
        <span class="selection-text" aria-live="polite">
      {{ selectedMessages.length }} {{ selectedMessages.length > 1 ? $t('CONVERSATION.CONTEXT_MENU.FORWARD.SELECTION_COUNTED') : $t('CONVERSATION.CONTEXT_MENU.FORWARD.SELECTION_COUNTED_SINGULAR') }}
    </span>
        <button
          class="forward-button"
          :title="$t('CONVERSATION.CONTEXT_MENU.FORWARD.TITLE')"
          @click="handleForward"
        >
          <svg class="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
            <path fill="currentColor" d="M14.278,4.813c0-0.723,0.873-1.085,1.383-0.574l6.045,6.051 c0.317,0.317,0.317,0.829,0,1.146l-6.045,6.051c-0.51,0.51-1.383,0.149-1.383-0.574v-2.732c-5.096,0-8.829,1.455-11.604,4.611 c-0.246,0.279-0.702,0.042-0.602-0.316C3.502,13.303,6.997,8.472,14.278,7.431V4.813z"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Forward Modal -->
    <woot-modal
      v-if="showForwardModal"
      v-model:show="showForwardModal"
      class="forward-modal"
      :on-close="closeForwardModal"
    >
      <div class="forward-modal-content h-full">
        <div class="modal-header p-6 
                    bg-n-slate-10 dark:bg-n-teal-2 
                    border-b border-n-slate-5 dark:border-n-slate-8
                    ">
          <h2 class="text-lg font-semibold text-white">
            Encaminhar para
          </h2>
        </div>
        <div class="modal-body p-4">
          <div class="contact-selector">
            <input
              ref="searchInput"
              v-model="searchQuery"
              type="text"
              placeholder="Buscar contatos..."
              class="w-full p-2 border border-n-slate-5 dark:border-n-slate-8 rounded-md focus:outline-none focus:ring-2 focus:ring-n-blue-5"
              @input="handleContactSearch($event.target.value)"
            />
            <div
              v-if="searchQuery && contacts.length === 0"
              class="mt-2 text-n-slate-6 dark:text-n-slate-4"
            >
              Nenhum resultado encontrado
            </div>

            <ul
              v-if="showContactsDropdown && contactsList.length"
              class="contact-list mt-2 max-h-[18rem] overflow-y-auto"
              @scroll="handleContactListScroll"
            >
              <template v-for="(item, index) in contactsList" :key="item.id || `divider-${index}`">
                <li v-if="item.isDivider" class="my-2">
                  <hr class="border-n-slate-5 dark:border-n-slate-8" />
                </li>
                <li
                  v-else
                  class="flex items-center p-2 hover:bg-n-slate-1 dark:hover:bg-n-slate-8 rounded-md cursor-pointer"
                >
                  <input
                    type="checkbox"
                    :checked="item.isChecked"
                    @change="toggleContactSelection(item)"
                    class="mr-2"
                  />
                  <img
                    v-if="item.thumbnail.src"
                    :src="item.thumbnail.src"
                    :alt="item.thumbnail.name"
                    class="w-8 h-8 rounded-full mr-2"
                  />
                  <div
                    v-else
                    class="w-8 h-8 rounded-full bg-n-slate-5 flex items-center justify-center mr-2"
                  >
                    <span class="text-n-slate-10 text-sm">{{ item.thumbnail.name.charAt(0) }}</span>
                  </div>
                  <div class="flex-1">
                    <span class="text-n-slate-9 dark:text-white">{{ item.label }}</span>
                    <span class="block text-sm text-n-slate-6 dark:text-white">
                      {{ item.email || item.phone_number }}
                    </span>
                  </div>
                </li>
              </template>
            </ul>
          </div>
        </div>
        
        <!-- Botões do Modal Footer Melhorados -->
        <div class="modal-footer p-6 flex justify-end gap-2 border-t border-n-slate-5 dark:border-n-slate-8 bg-n-slate-1 dark:bg-n-teal-2">
          <woot-button
            color-scheme="secondary"
            variant="clear"
            class="px-6 py-2.5 text-n-slate-11 dark:text-n-slate-10 hover:bg-n-slate-3 dark:hover:bg-n-slate-4 border border-n-slate-6 dark:border-n-slate-7 rounded-lg font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-n-slate-8 focus:ring-offset-2"
            @click="closeForwardModal"
          >
            Cancelar
          </woot-button>
          
          <woot-button
            color-scheme="primary"
            class="px-6 py-2.5 
                  bg-n-slate-10 text-n-slate-1 
                  hover:bg-n-slate-12 hover:text-white 
                  dark:bg-n-teal-4 dark:text-white 
                  dark:hover:bg-n-teal-6 
                  disabled:bg-n-slate-4 disabled:text-n-slate-8 
                  border-0 rounded-lg font-medium shadow-sm 
                  hover:shadow-md transition-all duration-200 
                  focus:outline-none focus:ring-2 focus:ring-woot-400 focus:ring-offset-2 
                  disabled:cursor-not-allowed disabled:shadow-none"

            @click="confirmForward"
            :disabled="!selectedContacts.length"
          >
            <span class="flex items-center gap-2">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
              </svg>
              Encaminhar
            </span>
          </woot-button>
        </div>

      </div>
    </woot-modal>
  </div>
</template>

<style scoped lang="scss">

.message-selection-panel {
  @apply flex relative flex-col bg-slate-200 dark:bg-slate-700;
  
  // Garantir que o painel seja sempre visível
  position: sticky;
  bottom: 0;
  z-index: 10;
  
  
  .selection-text {
    @apply text-sm font-semibold text-slate-600 dark:text-slate-100;
  }

  .forward-button {
    @apply p-2 rounded-full;
    margin-left: auto;
    svg {
      @apply w-6 h-6 text-sm;
    }
  }
}

.forward-modal {
  ::v-deep {
    .modal-container {
      @apply max-w-[26.5rem] h-[33rem] mx-auto rounded-lg bg-white dark:bg-woot-900 shadow-2xl;
    }

    .modal-header {
      @apply p-4 border-b border-n-slate-5 dark:border-n-slate-8;
    }

    .modal-body {
      @apply p-4 flex-1 overflow-y-auto;
    }

    .modal-footer {
      @apply p-4 flex justify-end gap-2 border-t border-n-slate-5 dark:border-n-slate-8;
    }
  }
}

.forward-modal-content {
  @apply flex flex-col h-full;

  .contact-selector {
    @apply flex-1 flex flex-col min-h-[18rem];
  }

  .contact-list {
    @apply max-h-[18rem] overflow-y-auto;
  }
}
</style>